// Layout.jsx — Optimized version (CLS + performance focused)
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Preloader from './PreLoader';
import MainSiteHeader from './MainSiteHeader';
import Lenis from '@studio-freight/lenis';
import FloatingCursor from './FloatingCursor';
import ScrollToTop from './ScrollToTop';

const Layout = () => {
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const lenisRef = useRef(null);

  // 0. Scroll reset on route change (for Lenis)
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [location.pathname]);

  // 1. Initialize Lenis ONCE (no dependency on loading)
  useEffect(() => {
    // Prevent multiple instances
    if (lenisRef.current) return;

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,           // better battery + less jank on touch
      touchMultiplier: 2,
      normalizeWheel: true,
      wheelMultiplier: 1,
      infinite: false,
    });

    lenisRef.current = lenis;

    // Lightweight RAF loop
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    // Start in stopped state
    lenis.stop();

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []); // ← empty deps = create once

  // 2. Control Lenis start/stop based on preloader state
  useEffect(() => {
    if (!lenisRef.current) return;

    if (loading) {
      lenisRef.current.stop();
      // Ensure no scroll during preloader
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      lenisRef.current.start();
      // Restore scroll
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    // Cleanup on unmount / state change
    return () => {
      if (lenisRef.current) {
        lenisRef.current.start(); // safety net
      }
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [loading]);

  // 3. Facebook SDK – lazy loaded only when needed (better CLS safety)
  useEffect(() => {
    // Optional: load only on user interaction (e.g. after scroll or click)
    // For now keeping simple async load, but consider moving to a social component
    const loadFacebookSDK = () => {
      if (document.getElementById('facebook-jssdk')) return;

      window.fbAsyncInit = function () {
        window.FB.init({
          xfbml: true,
          version: 'v20.0',
        });
      };

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    };

    // Delay load slightly or on interaction
    const timer = setTimeout(loadFacebookSDK, 3000); // or on scroll/click event

    return () => clearTimeout(timer);
  }, []);

  const handlePreloaderFinish = () => {
    setLoading(false);
  };

  return (
    <>
      <ScrollToTop />

      {/* Preloader – shown on top */}
      {loading && <Preloader onFinished={handlePreloaderFinish} />}

      {/* Cursor – always present, but hidden on mobile inside component */}
      <FloatingCursor />

      {/* Main content wrapper – no fade transition to avoid perceived shift */}
      <div
        style={{
          pointerEvents: loading ? 'none' : 'auto',
          // Removed opacity fade + transition → reduces potential paint/reflow
          // If you really want fade, use CSS class with shorter duration
        }}
      >
        <MainSiteHeader isPreloaderDone={!loading} />
        <Outlet context={{ lenis: lenisRef.current, isPreloaderDone: !loading }} />
      </div>

      {/* Global style override during loading – more reliable than inline */}
      {loading && (
        <style>{`
          html, body {
            overflow: hidden !important;
            height: 100%;
            touch-action: none; /* extra safety on touch devices */
          }
        `}</style>
      )}
    </>
  );
};

export default Layout;